import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import APIService from '../APIService';
import CacheService from '../CacheService';
import i18n from '../i18n';

const GameContext = createContext(null);

const GAME_CACHE_TTL = 60 * 60 * 1000; // 1 hour
const LEVELS_LOOK_AHEAD = 5; // must match server-side GameDataStore.LEVELS_LOOK_AHEAD

function getGameCacheKey() {
  try {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    return user?.id ? `gameCache_${user.id}` : null;
  } catch { return null; }
}

function readGameCache(lang) {
  try {
    const key = getGameCacheKey();
    if (!key) return null;
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const { data, timestamp, lang: cachedLang } = JSON.parse(raw);
    if (cachedLang !== lang) return null;
    if (Date.now() - timestamp > GAME_CACHE_TTL) return null;
    return data;
  } catch { return null; }
}

function writeGameCache(lang, data) {
  try {
    const key = getGameCacheKey();
    if (!key) return;
    localStorage.setItem(key, JSON.stringify({ data, lang, timestamp: Date.now() }));
  } catch {}
}

export function clearGameCache() {
  try {
    const key = getGameCacheKey();
    if (key) localStorage.removeItem(key);
  } catch {}
}

export function GameProvider({ children, initialDataPromise }) {
  const lang = i18n.language;
  const cache = readGameCache(lang);

  const [tests, setTests] = useState(() => {
    if (!cache?.tests) return null;
    const map = {};
    for (const t of cache.tests) map[t.id] = t;
    return map;
  });
  const [diagnoses, setDiagnoses] = useState(() => cache?.diagnoses || null);
  const [backgroundQuestions, setBackgroundQuestions] = useState(null);
  const [physicalExamItems, setPhysicalExamItems] = useState(null);
  const [levels, setLevels] = useState(() => cache?.sortedLevels || null);
  const [totalCaseCount, setTotalCaseCount] = useState(() => cache?.totalCaseCount ?? 0);
  const [caseReward, setCaseReward] = useState(() => cache?.caseReward ?? 200);
  const levelsRef = useRef(levels);
  const totalCaseCountRef = useRef(cache?.totalCaseCount ?? 0);
  const statsRef = useRef(cache?.stats || { correct: 0, totalEarned: 0 });
  const langRef = useRef(lang);
  const [gameDataLoading, setGameDataLoading] = useState(!cache);
  const [gameDataError, setGameDataError] = useState(null);

  const [balance, setBalance] = useState(() => cache ? (cache.balance ?? 0) : null);
  const [completedCases, setCompletedCases] = useState(() => cache?.completedCases || {});
  const [stats, setStats] = useState(() => cache?.stats || { correct: 0, totalEarned: 0 });
  const [wrongAttempts, setWrongAttempts] = useState(() => cache?.wrongAttempts ?? 0);
  const [unlockedLevel, setUnlockedLevel] = useState(() => cache?.unlockedLevel ?? 1);
  const [brokeAt, setBrokeAt] = useState(() => cache?.brokeAt ?? null);
  const [waitDurationMs, setWaitDurationMs] = useState(() => cache?.waitDurationMs ?? 30 * 60 * 1000);
  const usedInitialPromise = useRef(false);

  useEffect(() => { statsRef.current = stats; }, [stats]);
  useEffect(() => { langRef.current = lang; }, [lang]);

  useEffect(() => {
    if (!cache) setGameDataLoading(true);

    let cancelled = false;
    let dataPromise;

    if (initialDataPromise && !usedInitialPromise.current) {
      usedInitialPromise.current = true;
      dataPromise = initialDataPromise;
    } else {
      CacheService.invalidatePattern('gameTests');
      CacheService.invalidatePattern('gameDiagnoses');
      CacheService.invalidatePattern('gameLevels');
      CacheService.invalidatePattern('backgroundQuestions');
      CacheService.invalidatePattern('physicalExam');
      dataPromise = Promise.all([
        APIService.getTests(),
        APIService.getDiagnoses(),
        APIService.getLevels(),
        APIService.getGameProgress().catch(() => null),
        APIService.getBackgroundQuestions().catch(() => null),
        APIService.getPhysicalExam().catch(() => null),
      ]);
    }

    dataPromise
      .then(([testsRes, diagnosesRes, levelsRes, progressRes, bgQuestionsRes, physicalExamRes]) => {
        if (cancelled) return;

        const testsMap = {};
        for (const t of testsRes.tests) testsMap[t.id] = t;
        setTests(testsMap);
        setDiagnoses(diagnosesRes.diagnoses.map(d => d.name));
        if (bgQuestionsRes?.questions) setBackgroundQuestions(bgQuestionsRes.questions);
        if (physicalExamRes?.items) setPhysicalExamItems(physicalExamRes.items);

        const sorted = [...levelsRes.levels].sort((a, b) => a.level - b.level);
        setLevels(sorted);
        levelsRef.current = sorted;
        const totalCount = levelsRes.totalCaseCount ?? sorted.length;
        setTotalCaseCount(totalCount);
        totalCaseCountRef.current = totalCount;
        setCaseReward(levelsRes.caseReward);
        const waitMs = levelsRes.waitDurationMs || 30 * 60 * 1000;
        setWaitDurationMs(waitMs);

        let balanceValue = levelsRes.startingBalance;
        let casesToSave = {};
        let statsToSave = { correct: 0, totalEarned: 0 };
        let wrongToSave = 0;
        let unlockedToSave = 1;
        let brokeAtToSave = null;

        if (progressRes) {
          unlockedToSave = progressRes.unlockedLevel || 1;
          casesToSave = progressRes.completedCases || {};
          const totalEarned = Object.values(casesToSave).reduce((sum, c) => sum + (c.earned || 0), 0);
          const correct = Object.values(casesToSave).filter(c => c.correct).length;
          statsToSave = { correct, totalEarned };
          wrongToSave = progressRes.wrongAttempts || 0;
          balanceValue = progressRes.balance;
          brokeAtToSave = progressRes.brokeAt ?? null;
        }

        setUnlockedLevel(unlockedToSave);
        setCompletedCases(casesToSave);
        setStats(statsToSave);
        setWrongAttempts(wrongToSave);
        setBalance(balanceValue);
        setBrokeAt(brokeAtToSave);

        writeGameCache(lang, {
          tests: testsRes.tests,
          sortedLevels: sorted,
          totalCaseCount: totalCount,
          diagnoses: diagnosesRes.diagnoses.map(d => d.name),
          caseReward: levelsRes.caseReward,
          waitDurationMs: waitMs,
          balance: balanceValue,
          completedCases: casesToSave,
          stats: statsToSave,
          wrongAttempts: wrongToSave,
          unlockedLevel: unlockedToSave,
          brokeAt: brokeAtToSave,
        });

        setGameDataLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.isAuthError) return; // redirect already triggered
        if (!cache) {
          setGameDataError(err.message || 'Failed to load game data');
        }
        setGameDataLoading(false);
      });

    return () => {
      cancelled = true;
      usedInitialPromise.current = false;
    };
  }, [lang]); // eslint-disable-line react-hooks/exhaustive-deps

  const deductBalance = useCallback((amount) => {
    setBalance((prev) => {
      const newBalance = prev - amount;
      if (newBalance <= 0) {
        setBrokeAt((curr) => curr ?? Date.now());
      }
      return newBalance;
    });
  }, []);

  const applySubmissionResult = useCallback((result) => {
    const { correct, earned, unlockedLevel: newUnlockedLevel, completedCases: newCases,
            wrongAttempts: newWrong, balance: newBalance, brokeAt: newBrokeAt,
            waitDurationMs: newWaitMs } = result;

    setCompletedCases(newCases);
    setBalance(newBalance);
    setUnlockedLevel(newUnlockedLevel);
    setWrongAttempts(newWrong);
    setBrokeAt(newBrokeAt ?? null);
    if (newWaitMs) setWaitDurationMs(newWaitMs);

    const newStats = correct
      ? { correct: statsRef.current.correct + 1, totalEarned: statsRef.current.totalEarned + earned }
      : statsRef.current;
    if (correct) setStats(() => newStats);

    const existingCache = readGameCache(langRef.current);
    if (existingCache) {
      writeGameCache(langRef.current, {
        ...existingCache,
        wrongAttempts: newWrong,
        balance: newBalance,
        completedCases: newCases,
        unlockedLevel: newUnlockedLevel,
        brokeAt: newBrokeAt ?? null,
        stats: newStats,
      });
    }

    if (correct) {
      const loadedCount = levelsRef.current?.length ?? 0;
      const total = totalCaseCountRef.current;
      if (total > loadedCount && newUnlockedLevel + LEVELS_LOOK_AHEAD > loadedCount) {
        CacheService.invalidatePattern('gameLevels');
        APIService.getLevels()
          .then(res => {
            const sorted = [...res.levels].sort((a, b) => a.level - b.level);
            setLevels(sorted);
            levelsRef.current = sorted;
            if (res.totalCaseCount != null) {
              setTotalCaseCount(res.totalCaseCount);
              totalCaseCountRef.current = res.totalCaseCount;
            }
          })
          .catch(() => {});
      }
    }
  }, []);

  const canPlay = !brokeAt || (Date.now() > brokeAt + waitDurationMs);

  return (
    <GameContext.Provider value={{
      tests, diagnoses, backgroundQuestions, physicalExamItems, levels, totalCaseCount, caseReward,
      gameDataLoading, gameDataError,
      balance, completedCases, stats, wrongAttempts, unlockedLevel,
      brokeAt, waitDurationMs, canPlay,
      deductBalance, applySubmissionResult,
    }}>
      {children}
    </GameContext.Provider>
  );
}

export function useGame() {
  const ctx = useContext(GameContext);
  if (!ctx) throw new Error('useGame must be used inside GameProvider');
  return ctx;
}
